import './styles/graphic.scss';
import mapboxgl from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import updateLocale from 'dayjs/plugin/updateLocale';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(updateLocale);

dayjs.updateLocale('en', {
  monthsShort: [
    'Jan.', 'Feb.', 'March', 'April', 'May', 'June',
    'July', 'Aug.', 'Sept.', 'Oct.', 'Nov.', 'Dec.',
  ],
  meridiem: (hour, minute, isLowercase) => (hour >= 12 ? 'p.m.' : 'a.m.'),
});

const drawMap = (container, isMobile) => {
  mapboxgl.accessToken = 'pk.eyJ1IjoiY25uZGlnaXRhbCIsImEiOiJiYjIwMTgxNDZmNjgyODllMjAxYTYwOTAxYmY2MzI2MyJ9.8F6TQITXymFzCXy8tBqGgg';

  // screen size config
  const optionsSm = {
    initZoom: 2,
    minZoom: 2,
    maxZoom: 6,
    center: [-95.5, 38.5],
    circleRadius: 4,
    maxBounds: [[-180, 0], [-20, 52]],
  };

  const optionsLg = {
    initZoom: 3.2,
    minZoom: 2,
    maxZoom: 6,
    center: [-97, 38.5],
    circleRadius: 6,
    maxBounds: [[-180, 0], [-20, 52]],
  };

  const options = isMobile ? optionsSm : optionsLg;

  const map = new mapboxgl.Map({
    container: container.node(),
    style: 'mapbox://styles/cnndigital/clx2alq9r01mp01q13ulh4c2a',
    center: options.center,
    zoom: options.initZoom,
    maxZoom: options.maxZoom,
    minZoom: options.minZoom,
    maxBounds: options.maxBounds,
  });

  map.dragRotate.disable();
  map.touchZoomRotate.disableRotation();
  map.scrollZoom.disable();

  if (!isMobile) {
    const nav = new mapboxgl.NavigationControl({
      showCompass: false,
    });
    map.addControl(nav, 'top-left');
  }

  // Log the initial zoom level
  console.log(`Initial zoom level: ${map.getZoom()}`);

  // Add an event listener to log the zoom level whenever it changes
  map.on('zoom', () => {
    console.log(`Zoom level changed to: ${map.getZoom()}`);
  });

  // Function to add the heat records layer
  const addHeatRecordsLayer = (geojson) => {
    if (map.getSource('heat-records')) {
      map.getSource('heat-records').setData(geojson);
    } else {
      map.addSource('heat-records', {
        type: 'geojson',
        data: geojson,
      });

      map.addLayer({
        id: 'heat-records-layer',
        type: 'circle',
        source: 'heat-records',
        paint: {
          'circle-radius': options.circleRadius,
          'circle-color': '#c52622',
          'circle-opacity': 0.7,
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 1,
        },
      });

      map.on('click', 'heat-records-layer', (e) => {
        const coordinates = e.features[0].geometry.coordinates.slice();
        const {
          date, location, state, himax, diff_normal,
        } = e.features[0].properties;

        const dateFormatted = dayjs.utc(date).format('MMM D, YYYY');

        new mapboxgl.Popup()
          .setLngLat(coordinates)
          .setHTML(`<strong>${location}, ${state}</strong><p>The temperature at this station on ${dateFormatted} reached <b>${himax}°F</b>, a record for this calendar day.</p>`)
          .addTo(map);
      });

      map.on('mouseenter', 'heat-records-layer', () => {
        map.getCanvas().style.cursor = 'pointer';
      });

      map.on('mouseleave', 'heat-records-layer', () => {
        map.getCanvas().style.cursor = '';
      });
    }
  };

  // Function to fetch and process data
  const fetchDataAndAddLayer = () => {
    fetch('https://ix.cnn.io/data/heat-records-observed/observed_heat_records.json')
      .then(response => response.json())
      .then((data) => {
        const heatRecords = data.data;
        const { msg } = data;
        const msgExtended = data.msg_extended;

        // Update the content of the element with the class 'subhed dek'
        const subhedElement = document.querySelector('.subhed.dek');
        if (subhedElement) {
          subhedElement.innerHTML = `${msgExtended}`;
        }

        const geojson = {
          type: 'FeatureCollection',
          features: heatRecords.map(record => ({
            type: 'Feature',
            properties: {
              date: record.date,
              location: record.location,
              state: record.state,
              himax: record.himax,
              diff_normal: record.diff_normal,
            },
            geometry: {
              type: 'Point',
              coordinates: [record.lon, record.lat],
            },
          })),
        };

        addHeatRecordsLayer(geojson);

        const timestamp = data.lastUpdatedStr;
        document.getElementById('timestamp').textContent = `Last updated: ${timestamp}`;
      })
      .catch((error) => {
        console.error('Error fetching data:', error);
      });
  };

  map.on('load', () => {
    fetchDataAndAddLayer();
  });

  // Retry fetching data and adding the layer if the initial load fails
  map.on('idle', () => {
    if (!map.getLayer('heat-records-layer')) {
      fetchDataAndAddLayer();
    }
  });
};

export default function (props, container, isMobile) {
  const {
    initialWidth,
    initialHeight,
  } = props;

  container.html(''); // Clear previous content
  container.append('div')
    .attr('id', 'map')
    .style('width', `${initialWidth}px`)
    .style('height', `${initialHeight}px`);

  drawMap(container.select('#map'), isMobile); // Pass the D3 selection of the map div
}
